import { SERVICE_ID } from '../config';
import { localizeField } from './mapOrder';
import { parseProductPrice } from './price';

/** Crystal Bardawam (service_id 2) city ids from API. */
const ZONES = [
  { cityId: 5, name: 'هەولێر', minLat: 36.05, maxLat: 36.35, minLng: 43.82, maxLng: 44.22 },
  { cityId: 6, name: 'کوردستان', minLat: 34.75, maxLat: 37.45, minLng: 42.2, maxLng: 46.15 },
  { cityId: 7, name: 'عێراق', minLat: 29.06, maxLat: 37.39, minLng: 38.79, maxLng: 48.61 },
];

/** Cities that should not appear for this storefront (e.g. Mosul). */
const EXCLUDED_CITY_IDS = new Set([8]);

export function pointInZone(latitude, longitude, zone) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  return lat >= zone.minLat && lat <= zone.maxLat && lng >= zone.minLng && lng <= zone.maxLng;
}

export function resolveCityIdFromCoords(latitude, longitude) {
  for (const zone of ZONES) {
    if (pointInZone(latitude, longitude, zone)) {
      return zone.cityId;
    }
  }
  return null;
}

export function findCityById(cities, cityId) {
  if (!Array.isArray(cities) || cityId == null) return null;
  return cities.find((city) => Number(city.id) === Number(cityId)) ?? null;
}

export function resolveCityFromCoords(cities, latitude, longitude) {
  const cityId = resolveCityIdFromCoords(latitude, longitude);
  if (cityId == null) return null;
  return findCityById(cities, cityId);
}

function extractList(json) {
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json)) return json;
  return [];
}

/** Keep only Crystal Bardawam (service_id 2) rows. */
export function filterServiceItems(list, serviceId = SERVICE_ID) {
  if (!Array.isArray(list)) return [];
  return list.filter((item) => {
    if (item?.service_id == null) return true;
    return Number(item.service_id) === Number(serviceId);
  });
}

export function extractServiceList(json, serviceId = SERVICE_ID) {
  return filterServiceItems(extractList(json), serviceId);
}

/** Cities for Crystal only, excluding unused zones like Mosul (id 8). */
export function extractServiceCities(json, serviceId = SERVICE_ID) {
  return extractServiceList(json, serviceId).filter(
    (city) => !EXCLUDED_CITY_IDS.has(Number(city.id)),
  );
}

export function getCityName(city, language) {
  return localizeField(city?.name, language);
}

export { extractList };

export function calculateDeliveryTotals({ subtotal, city, offers }) {
  const productsSubtotal = Number(subtotal) || 0;
  const cityDelivery = parseProductPrice(city?.delivery_price);
  const list = Array.isArray(offers) ? offers : [];

  const matched = list
    .filter((offer) => productsSubtotal >= parseProductPrice(offer.min_amount))
    .sort((a, b) => parseProductPrice(b.min_amount) - parseProductPrice(a.min_amount));

  const bestOffer = matched[0] ?? null;
  const hasFreeDelivery = matched.length > 0;
  const offerDiscountPercent = matched.reduce((max, offer) => {
    const pct = parseProductPrice(offer.discount_percent);
    return pct > max ? pct : max;
  }, 0);

  const deliveryFee = hasFreeDelivery ? 0 : cityDelivery;
  const offerDiscountAmount = (productsSubtotal * offerDiscountPercent) / 100;
  const productsAfterOffer = Math.max(0, productsSubtotal - offerDiscountAmount);
  const grandTotal = productsAfterOffer + deliveryFee;

  return {
    productsSubtotal,
    cityDelivery,
    deliveryFee,
    hasFreeDelivery,
    offerDiscountPercent,
    offerDiscountAmount,
    productsAfterOffer,
    grandTotal,
    bestOffer,
    matchedOffers: matched,
  };
}
